import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';

interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
  required?: boolean;
}

/**
 * A password input with a reveal toggle.
 *
 * The toggle is not a nicety here. Staff type these on cheap Android handsets, one-handed,
 * often with gloves on and in a plant room with bad light — conditions where a field that
 * shows nothing but dots turns one mistyped character into a failed sign-in with no way to
 * see why. That now costs more than it used to: repeated failures trip a cooldown
 * (SPEC.md §9.5), so being able to check what was typed is what keeps someone from
 * throttling themselves out of reporting a fault.
 *
 * Visibility starts off and lives in component state, so it never persists past the
 * screen. On a shared handset the next person gets dots again without having to think
 * about it.
 */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete = 'current-password',
  required,
}: PasswordFieldProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <label className="block">
      <span className="mb-1 block text-sm text-slate-300">{label}</span>
      <div className="relative">
        <input
          // The whole point of the control: swapping the type is what reveals the value.
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          autoComplete={autoComplete}
          // Room for the button, so a long password never slides underneath it.
          className="w-full rounded-lg border border-slate-700 bg-slate-900 py-3 pl-3 pr-14 text-base"
        />
        <button
          // Explicitly not a submit: inside a form, a bare button would send it, so
          // glancing at the password would attempt a sign-in with whatever is typed.
          type="button"
          onClick={() => setVisible((shown) => !shown)}
          // The state belongs on the control rather than in the label text, so a screen
          // reader announces the toggle rather than reading a button whose name changes.
          aria-pressed={visible}
          aria-label={visible ? t('login.hidePassword') : t('login.showPassword')}
          // 56px square. CLAUDE.md sets a 48px floor for tap targets and this one is hit
          // with a gloved thumb, next to an input it must not be mistaken for.
          className="absolute inset-y-0 right-0 flex min-h-[56px] w-14 items-center justify-center rounded-r-lg text-slate-400"
        >
          {visible ? (
            <EyeOff className="size-5" aria-hidden="true" />
          ) : (
            <Eye className="size-5" aria-hidden="true" />
          )}
        </button>
      </div>
    </label>
  );
}
